"use client";

import React, { useRef } from "react";
import { SectionContentProps } from "../../types";
import EditableText from "../../core/EditableText";
import { PiCamera, PiArrowUpRight } from "react-icons/pi";
import homepageApi from "@/services/homepageApi";
import toast from "react-hot-toast";
import logger from "@/utils/logger";

export default function CollegeAbout({
  content,
  onContentChange,
  isSelected,
}: SectionContentProps) {
  const c = content || {};
  const primaryColor = c.colorPalette?.primary || "#1E3A8A";
  const fontFamily = c.fontFamily || "'Inter', sans-serif";

  const img1InputRef = useRef<HTMLInputElement>(null);
  const img2InputRef = useRef<HTMLInputElement>(null);

  const title = c.title || "ABOUT OUR COLLEGE";
  const description = c.description || "At our College, we believe in the transformative power of education and the boundless potential within every individual. Established in 1971, we have been dedicated to fostering intellectual curiosity, academic excellence, and a vibrant campus community.";
  const establishedText = c.establishedText || "EST. 1971";
  const circleText = c.circleText || "* Explore Future *College * Estd. 1971 ";
  const ctaText = c.ctaText || "College Overview";
  const ctaLink = c.ctaLink || "#";

  const imageUrl1 = c.imageUrl || "https://images.unsplash.com/photo-1541339907198-e08756dedf3f?w=600&h=800&fit=crop";
  const imageUrl2 = c.imageUrl2 || "https://images.unsplash.com/photo-1523050854058-8df90110c9f1?w=600&h=600&fit=crop";

  // Stats
  const stat1Value = c.stat1Value || "12,000";
  const stat1Label = c.stat1Label || "Students Enrolled";
  const stat2Value = c.stat2Value || "450+";
  const stat2Label = c.stat2Label || "Faculty & Staff";
  const stat3Value = c.stat3Value || "95%";
  const stat3Label = c.stat3Label || "Employment Rate";
  const stat4Value = c.stat4Value || "50K+";
  const stat4Label = c.stat4Label || "Alumni Worldwide";

  const handleImageUpload = async (file: File, fieldKey: "imageUrl" | "imageUrl2") => {
    if (file.size > 5 * 1024 * 1024) {
      toast.error("Image size must be less than 5MB");
      return;
    }

    const uploadPromise = homepageApi.uploadImage(file);
    toast.promise(uploadPromise, {
      loading: "Uploading image...",
      success: "Image uploaded successfully!",
      error: "Failed to upload image",
    });

    try {
      const response = await uploadPromise;
      if (response.success && response.data?.url) {
        onContentChange?.({
          ...c,
          [fieldKey === "imageUrl" ? "imageUrl" : "imageUrl2"]: response.data.url
        });
      }
    } catch (err) {
      logger.error(err);
    }
  };

  return (
    <section
      className={`py-24 px-6 md:px-12 bg-white transition-all ${isSelected ? "ring-2 ring-blue-500 relative z-20" : ""}`}
      style={{ fontFamily }}
    >
      <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
        {/* Left Side: Images & Established Badge */}
        <div className="relative flex items-center justify-center">
          <div className="relative w-full max-w-[480px] aspect-[4/5] rounded-2xl overflow-hidden shadow-xl bg-slate-100 group">
            <img src={imageUrl1} alt="About campus" className="w-full h-full object-cover" />
            {onContentChange && (
              <button
                onClick={() => img1InputRef.current?.click()}
                className="absolute inset-0 bg-black/45 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
              >
                <PiCamera className="w-8 h-8 text-white" />
              </button>
            )}
            <input
              type="file"
              ref={img1InputRef}
              className="hidden"
              accept="image/*"
              onChange={(e) => e.target.files?.[0] && handleImageUpload(e.target.files[0], "imageUrl")}
            />
          </div>

          {/* Overlapping Small Image */}
          <div className="absolute -bottom-8 -right-4 w-48 h-48 rounded-2xl overflow-hidden shadow-2xl border-4 border-white bg-slate-100 hidden sm:block group">
            <img src={imageUrl2} alt="Students" className="w-full h-full object-cover" />
            {onContentChange && (
              <button
                onClick={() => img2InputRef.current?.click()}
                className="absolute inset-0 bg-black/45 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
              >
                <PiCamera className="w-6 h-6 text-white" />
              </button>
            )}
            <input
              type="file"
              ref={img2InputRef}
              className="hidden"
              accept="image/*"
              onChange={(e) => e.target.files?.[0] && handleImageUpload(e.target.files[0], "imageUrl2")}
            />
          </div>

          {/* Established Year Stamp */}
          <div
            className="absolute top-6 left-6 p-4 rounded-xl text-white font-bold text-center shadow-lg"
            style={{ backgroundColor: primaryColor }}
          >
            <p className="text-2xl uppercase tracking-wider">{establishedText}</p>
          </div>
        </div>

        {/* Right Side: Text details & Stats */}
        <div className="space-y-6">
          <h2 className="text-3xl md:text-5xl font-extrabold text-slate-900 leading-tight">
            <EditableText value={title} onChange={(val) => onContentChange?.({ ...c, title: val })} as="span" />
          </h2>
          <p className="text-slate-600 text-lg leading-relaxed">
            <EditableText value={description} onChange={(val) => onContentChange?.({ ...c, description: val })} as="span" />
          </p>

          {/* Stats grid */}
          <div className="grid grid-cols-2 gap-6 pt-4 border-t border-slate-100">
            <div>
              <p className="text-3xl font-extrabold text-slate-900">{stat1Value}</p>
              <p className="text-sm text-slate-500 font-medium">{stat1Label}</p>
            </div>
            <div>
              <p className="text-3xl font-extrabold text-slate-900">{stat2Value}</p>
              <p className="text-sm text-slate-500 font-medium">{stat2Label}</p>
            </div>
            <div>
              <p className="text-3xl font-extrabold text-slate-900">{stat3Value}</p>
              <p className="text-sm text-slate-500 font-medium">{stat3Label}</p>
            </div>
            <div>
              <p className="text-3xl font-extrabold text-slate-900">{stat4Value}</p>
              <p className="text-sm text-slate-500 font-medium">{stat4Label}</p>
            </div>
          </div>

          <div className="pt-4">
            <a
              href={ctaLink}
              className="inline-flex items-center gap-2 font-bold text-sm hover:underline"
              style={{ color: primaryColor }}
            >
              <span>{ctaText}</span>
              <PiArrowUpRight className="w-4 h-4" />
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}
